import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccessibility } from '../hooks/useAccessibility';
import {
  Clock,
  FileText,
  CheckCircle,
  Mic,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Play,
  Zap,
  Target,
} from 'lucide-react';
import { SpeechControls } from './SpeechControls';
import { formatTime, formatProcessingTime } from '../utils/timeFormatter';

interface VideoSummaryData {
  summary: {
    title: string;
    summary: string;
    keyPoints: string[];
    duration: string;
    transcriptSource: 'official' | 'whisper';
  };
  videoUrl: string;
  processingTimeMs: number;
  // Métriques de temps
  originalLength: number;
  summaryLength: number;
  compressionRatio: number;
  timeToReadOriginal: number;
  timeToReadSummary: number;
  timeSaved: number;
  timeSavedPercentage: number;
  efficiency: string;
  timeSavedFormatted: string;
  totalTimeSaved: number;
  totalTimeSavedFormatted: string;
}

interface VideoSummaryResultProps {
  data: VideoSummaryData;
}

export default function VideoSummaryResult({ data }: VideoSummaryResultProps) {
  const { t } = useTranslation();
  const { announceToScreenReader } = useAccessibility();
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    keyPoints: true,
    details: false,
  });

  // Fonction pour extraire l'ID vidéo et générer l'URL de la miniature
  const getVideoThumbnail = (videoUrl: string): string => {
    const regex =
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^&\n?#]+)/;
    const match = videoUrl.match(regex);
    return match
      ? `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`
      : '';
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(type);
      announceToScreenReader(`${type} copié dans le presse-papiers`);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  const getEfficiencyColor = (efficiency: string): string => {
    switch (efficiency) {
      case 'Excellente':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Très bonne':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Bonne':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Correcte':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Faible':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTranscriptSourceInfo = (source: 'official' | 'whisper') => {
    if (source === 'official') {
      return {
        icon: <CheckCircle className="h-4 w-4 text-green-600" />,
        label: t('video.result.transcriptSource.official'),
        description: t('video.result.transcriptSource.officialDescription'),
        color: 'text-green-600',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        borderColor: 'border-green-200 dark:border-green-800',
      };
    } else {
      return {
        icon: <Mic className="h-4 w-4 text-blue-600" />,
        label: t('video.result.transcriptSource.whisper'),
        description: t('video.result.transcriptSource.whisperDescription'),
        color: 'text-blue-600',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        borderColor: 'border-blue-200 dark:border-blue-800',
      };
    }
  };

  const transcriptInfo = getTranscriptSourceInfo(data.summary.transcriptSource);

  return (
    <div className="space-y-6">
      {/* En-tête avec informations de la vidéo */}
      <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
              <img
                src={getVideoThumbnail(data.videoUrl)}
                alt="Miniature de la vidéo"
                className="w-16 h-10 sm:w-20 sm:h-12 object-cover rounded-lg flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                {data.summary.title}
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>{data.summary.duration}</span>
              </div>
              <div className="flex items-center gap-1">
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>
                  {t('video.result.processingTime')}{' '}
                  {formatProcessingTime(data.processingTimeMs)}
                </span>
              </div>
            </div>
          </div>
          <a
            href={data.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-2 text-xs sm:text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex-shrink-0"
          >
            <Play className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">
              {t('video.result.watchVideo')}
            </span>
            <span className="sm:hidden">Voir</span>
            <ExternalLink className="h-2 w-2 sm:h-3 sm:w-3" />
          </a>
        </div>
      </div>

      {/* Résumé principal */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <button
          onClick={() => toggleSection('summary')}
          className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('video.result.title')}
          </h3>
          <div className="flex items-center gap-2">
            <SpeechControls
              text={data.summary.summary}
              title="le résumé vidéo"
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(data.summary.summary, t('video.result.title'));
              }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              aria-label={t('video.result.copySummary')}
            >
              <Copy className="h-4 w-4" />
            </button>
            {expandedSections.summary ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </button>

        {expandedSections.summary && (
          <div className="px-6 pb-6">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {data.summary.summary}
              </p>
            </div>
            {copiedText === t('video.result.title') && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                ✓ {t('video.result.title')} {t('video.result.copied')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Métriques de temps */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:gap-4">
        {/* Temps de lecture original */}
        <div className="flex-1 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg sm:rounded-xl border border-red-200 dark:border-red-800 p-3 sm:p-4 lg:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-1.5 sm:p-2 bg-red-100 dark:bg-red-900/30 rounded-lg flex-shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-red-700 dark:text-red-300">
                Durée de visionnage
              </p>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-red-900 dark:text-red-100">
                {data.summary.duration}
              </p>
            </div>
          </div>
          <p className="text-xs text-red-600 dark:text-red-400">
            {data.originalLength ? data.originalLength.toLocaleString() : '0'}{' '}
            caractères du transcript
          </p>
        </div>

        {/* Temps de lecture résumé */}
        <div className="flex-1 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg sm:rounded-xl border border-green-200 dark:border-green-800 p-3 sm:p-4 lg:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/30 rounded-lg flex-shrink-0">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-green-700 dark:text-green-300">
                Temps de lecture du résumé
              </p>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-green-900 dark:text-green-100">
                {formatTime(data.timeToReadSummary)}
              </p>
            </div>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400">
            {data.summaryLength.toLocaleString()} caractères
          </p>
        </div>

        {/* Temps économisé */}
        <div className="flex-1 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg sm:rounded-xl border border-blue-200 dark:border-blue-800 p-3 sm:p-4 lg:p-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
              <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-300">
                Temps économisé
              </p>
              <p className="text-base sm:text-lg lg:text-xl font-bold text-blue-900 dark:text-blue-100">
                {formatTime(data.timeSaved)}
              </p>
            </div>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400">
            {data.timeSavedPercentage}% d'efficacité
          </p>
        </div>
      </div>

      {/* Statistiques */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Statistiques
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {data.compressionRatio.toFixed(1)}x
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Compression
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
              {data.timeSavedPercentage}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Temps économisé
            </div>
          </div>
          <div className="text-center">
            <div
              className={`px-3 py-1 rounded-full text-sm font-bold ${getEfficiencyColor(data.efficiency)}`}
            >
              {data.efficiency}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Efficacité
            </div>
          </div>
        </div>
      </div>

      {/* Points clés */}
      {data.summary.keyPoints.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <button
            onClick={() => toggleSection('keyPoints')}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('video.result.keyPoints')} ({data.summary.keyPoints.length})
            </h3>
            <div className="flex items-center gap-2">
              <SpeechControls
                text={data.summary.keyPoints
                  .map((point, index) => `${index + 1}. ${point}`)
                  .join('\n')}
                title="les points clés"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const pointsText = data.summary.keyPoints
                    .map((point, index) => `${index + 1}. ${point}`)
                    .join('\n');
                  copyToClipboard(pointsText, t('video.result.keyPoints'));
                }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                aria-label={t('video.result.copyKeyPoints')}
              >
                <Copy className="h-4 w-4" />
              </button>
              {expandedSections.keyPoints ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>

          {expandedSections.keyPoints && (
            <div className="px-6 pb-6">
              <ul className="space-y-3">
                {data.summary.keyPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 leading-relaxed">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
              {copiedText === t('video.result.keyPoints') && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-3">
                  ✓ {t('video.result.keyPoints')} {t('video.result.copied')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Détails techniques */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <button
          onClick={() => toggleSection('details')}
          className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('video.result.technicalDetails')}
          </h3>
          {expandedSections.details ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {expandedSections.details && (
          <div className="px-6 pb-6">
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="font-medium text-gray-700 dark:text-gray-300">
                  {t('video.result.videoUrl')}
                </dt>
                <dd className="text-gray-600 dark:text-gray-400 break-all">
                  {data.videoUrl}
                </dd>
              </div>
              <div>
                <dt className="font-medium text-gray-700 dark:text-gray-300">
                  {t('video.result.duration')}
                </dt>
                <dd className="text-gray-600 dark:text-gray-400">
                  {data.summary.duration}
                </dd>
              </div>
              <div>
                <dt className="font-medium text-gray-700 dark:text-gray-300">
                  {t('video.result.processingTime')}
                </dt>
                <dd className="text-gray-600 dark:text-gray-400">
                  {formatProcessingTime(data.processingTimeMs)}
                </dd>
              </div>
              <div>
                <dt className="font-medium text-gray-700 dark:text-gray-300">
                  {t('video.result.transcriptSource.source')}
                </dt>
                <dd className="text-gray-600 dark:text-gray-400">
                  {transcriptInfo.label}
                </dd>
              </div>
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
